import { useState } from "react";
import { MdCategory, MdExpandLess, MdExpandMore } from "react-icons/md";
import {
    Container,
    Content,
    Header,
    HeaderTitle,
    OperationAdherence,
    OperationClass,
    OperationClassAdherence,
    OperationClassContent,
    OperationClassCount,
    OperationClassHeader,
    OperationClassIcon,
    OperationClassMeta,
    OperationClassTitle,
    OperationCode,
    OperationHeader,
    OperationItem,
    OperationName,
    OperationStats,
    OperationsList,
    ToggleButton,
} from "./styles";
import useRelatorioContext from "ui/pages/app/planejamentoOperacional/hooks/useRelatorioContext";

type AdherenceLevel = "high" | "medium" | "low";

interface Operation {
    Operacao: string;
    Porcentagem: number;
    QuantidadeTalhoes: number;
}

function getAdherenceLevel(adherence: number): AdherenceLevel {
    if (adherence < 70) return "low";
    if (adherence < 85) return "medium";
    return "high";
}

function getAverageAdherence(operations: Operation[]) {
    return Math.round(
        operations.reduce((sum, op) => sum + op.Porcentagem, 0) / operations.length
    );
}

function parseOperationName(name: string) {
    const parts = name.split(" - ");
    const code = parts[0];
    const label = parts.slice(1).join(" - ");
    return { code, label };
}

type Props = {
    resumoPorOperacaoClasse: {
        OperacaoClasse: string;
        QuantidadeOperacoes: number;
        Porcentagem: number;
        Operacoes: {
            Operacao: string;
            Porcentagem: number;
            QuantidadeTalhoes: number;
        }[]
    }[]
}

export function ResumoPorClasseOperacao({ resumoPorOperacaoClasse }: Props) {
    const [panelExpanded, setPanelExpanded] = useState(false);
    const [expandedClasses, setExpandedClasses] = useState<Record<number, boolean>>({});
    const { showLegendsRelatorio } = useRelatorioContext();

    function togglePanel() {
        setPanelExpanded((prev) => !prev);
    }

    function toggleClass(classIndex: number) {
        setExpandedClasses((prev) => ({
            ...prev,
            [classIndex]: !prev[classIndex],
        }));
    }   

    if (!showLegendsRelatorio) return null;

    function formatarNumero(numero: number) {
        return Number.isInteger(numero) ? numero : Number(numero.toFixed(2));
    }

    return (
        <Container $expanded={panelExpanded} $minimized={!panelExpanded}>
            <Header>
                <HeaderTitle>
                    <MdCategory />
                    Resumo por Classe de Operação
                </HeaderTitle>
                <ToggleButton
                    type="button"
                    onClick={togglePanel}
                    aria-label="Alternar resumo por classe de operação"
                >
                    {panelExpanded ? <MdExpandMore /> : <MdExpandLess />}
                </ToggleButton>
            </Header>

            <Content $expanded={panelExpanded}>
                <OperationsList>
                    {resumoPorOperacaoClasse.map((operationClass, classIndex) => {
                        const avgAdherence = getAverageAdherence(operationClass.Operacoes);
                        const adherenceLevel = getAdherenceLevel(avgAdherence);
                        const isClassExpanded = !!expandedClasses[classIndex];

                        return (
                            <OperationClass key={operationClass.OperacaoClasse}>
                                <OperationClassHeader onClick={() => toggleClass(classIndex)}>
                                    <OperationClassTitle>
                                        <OperationClassIcon $expanded={isClassExpanded}>
                                            <MdExpandLess />
                                        </OperationClassIcon>
                                        {operationClass.OperacaoClasse}
                                    </OperationClassTitle>
                                    <OperationClassMeta>
                                        <OperationClassAdherence $level={adherenceLevel}>
                                            {formatarNumero(avgAdherence)}%
                                        </OperationClassAdherence>
                                        <OperationClassCount>
                                            {operationClass.Operacoes.length} operações
                                        </OperationClassCount>
                                    </OperationClassMeta>
                                </OperationClassHeader>

                                <OperationClassContent $expanded={isClassExpanded}>
                                    {operationClass.Operacoes.map((operation) => {
                                        const { code, label } = parseOperationName(operation.Operacao);
                                        const operationLevel = getAdherenceLevel(operation.Porcentagem);

                                        return (
                                            <OperationItem key={operation.Operacao}>
                                                <OperationHeader>
                                                    <OperationName>
                                                        <OperationCode>{code}</OperationCode>
                                                        {label}
                                                    </OperationName>
                                                    <OperationAdherence $level={operationLevel}>
                                                        {formatarNumero(operation.Porcentagem)}%
                                                    </OperationAdherence>
                                                </OperationHeader>
                                                <OperationStats>
                                                    {operation.QuantidadeTalhoes} talhões analisados
                                                </OperationStats>
                                            </OperationItem>
                                        );
                                    })}
                                </OperationClassContent>
                            </OperationClass>
                        );
                    })}
                </OperationsList>
            </Content>
        </Container>
    );
}
